import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import BoardLayoutTooltip from '../../shared/BoardLayoutTooltip';
import CardSortingTooltip from '../../shared/CardSortingTooltip';
import CoreQuillEditor from '../../shared/CoreQuillEditor';
import type {
  BoardCardSorting,
  BoardColumn,
  BoardLayout,
  BoardVotingStyle,
} from '../../../interfaces/BoardDetails';
import useBoardStore from '../../../store/boardStore';
import {
  DEFAULT_BOARD_COLUMN_COUNT,
  MAX_BOARD_COLUMNS,
  MIN_BOARD_COLUMNS,
  createDefaultBoardColumns,
  normalizeBoardColumns,
  resizeBoardColumns,
} from '../../../utils/boardColumns';

interface EditBoardDialogProps {
  open: boolean;
  onClose: () => void;
}

const EditBoardDialog = ({ open, onClose }: EditBoardDialogProps) => {
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const updateBoardDetails = useBoardStore((state) => state.updateBoardDetails);
  const actionError = useBoardStore((state) => state.actionError);
  const clearActionError = useBoardStore((state) => state.clearActionError);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [layout, setLayout] = useState<BoardLayout>('lanes');
  const [columns, setColumns] = useState<BoardColumn[]>(
    createDefaultBoardColumns(DEFAULT_BOARD_COLUMN_COUNT)
  );
  const [cardSorting, setCardSorting] = useState<BoardCardSorting>('custom');
  const [votingStyle, setVotingStyle] = useState<BoardVotingStyle>('dotvoting');
  const [votesPerUser, setVotesPerUser] = useState('0');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!boardDetails || !open) {
      return;
    }

    clearActionError();
    setTitle(boardDetails.title);
    setDescription(boardDetails.description);
    setLayout(boardDetails.layout);
    setColumns(normalizeBoardColumns(boardDetails.columns));
    setCardSorting(boardDetails.cardSorting === 'automatic' ? 'automatic' : 'custom');
    setVotingStyle(boardDetails.votingStyle);
    setVotesPerUser(String(boardDetails.votesPerUser));
    setIsSaving(false);
  }, [boardDetails, clearActionError, open]);

  if (!boardDetails) {
    return null;
  }

  const sanitizedTitle = title.trim();
  const canSave = sanitizedTitle.length > 0 && !isSaving;

  const onSave = async () => {
    if (!canSave) {
      return;
    }

    const parsedVotesPerUser = Number.parseInt(votesPerUser, 10);
    const nextVotesPerUser =
      votingStyle === 'dotvoting'
        ? Number.isNaN(parsedVotesPerUser)
          ? boardDetails.votesPerUser
          : Math.max(0, parsedVotesPerUser)
        : 0;
    setIsSaving(true);

    try {
      await updateBoardDetails({
        title: sanitizedTitle,
        description: description.trim(),
        layout,
        columns: layout === 'columns' ? columns : [],
        cardSorting,
        votingStyle,
        votesPerUser: nextVotesPerUser,
      });
      onClose();
    } catch {
      // The store records the user-facing error.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>{`Edit Board`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={isSaving}
          />
          <Box>
            <Box sx={{ fontSize: 14, mb: 0.75, color: 'text.secondary' }}>Description</Box>
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                '.ql-container': { border: 0 },
                '.ql-editor': { minHeight: 120 },
              }}
            >
              <CoreQuillEditor value={description} onChange={setDescription} />
            </Box>
          </Box>
          <Stack spacing={1}>
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
                <Box sx={{ fontSize: 14, color: 'text.secondary' }}>Card sorting</Box>
                <CardSortingTooltip />
              </Stack>
              <TextField
                select
                value={cardSorting}
                onChange={(event) => setCardSorting(event.target.value as BoardCardSorting)}
                disabled={isSaving}
                fullWidth
              >
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="automatic">Automatic</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
                <Box sx={{ fontSize: 14, color: 'text.secondary' }}>Layout</Box>
                <BoardLayoutTooltip />
              </Stack>
              <TextField
                select
                value={layout}
                onChange={(event) => {
                  const nextLayout = event.target.value as BoardLayout;
                  setLayout(nextLayout);
                  if (nextLayout === 'columns') {
                    setColumns((currentColumns) =>
                      resizeBoardColumns(
                        currentColumns,
                        currentColumns.length || DEFAULT_BOARD_COLUMN_COUNT
                      )
                    );
                  }
                }}
                disabled={isSaving}
                fullWidth
              >
                <MenuItem value="grid">Grid</MenuItem>
                <MenuItem value="lanes">Lanes</MenuItem>
                <MenuItem value="columns">Columns</MenuItem>
              </TextField>
            </Box>
            {layout === 'columns' && (
              <TextField
                type="number"
                label="Number of columns"
                value={columns.length}
                onChange={(event) => {
                  const nextCount = Number.parseInt(event.target.value, 10);
                  setColumns((currentColumns) => resizeBoardColumns(currentColumns, nextCount));
                }}
                inputProps={{ min: MIN_BOARD_COLUMNS, max: MAX_BOARD_COLUMNS }}
                disabled={isSaving}
                fullWidth
              />
            )}
            <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Voting style"
                  value={votingStyle}
                  onChange={(event) => {
                    const nextVotingStyle = event.target.value as BoardVotingStyle;
                    setVotingStyle(nextVotingStyle);
                  }}
                  disabled={isSaving}
                  fullWidth
                >
                  <MenuItem value="dotvoting">Dot voting</MenuItem>
                  <MenuItem value="liking">Liking</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </TextField>
              </Box>
              {votingStyle === 'dotvoting' && (
                <Box sx={{ flex: 1 }}>
                  <TextField
                    type="number"
                    label="Votes per user"
                    value={votesPerUser}
                    onChange={(event) => setVotesPerUser(event.target.value)}
                    inputProps={{ min: 0 }}
                    disabled={isSaving}
                    fullWidth
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={!canSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBoardDialog;
